import { Params } from "@angular/router";
import { decodeBase64RouteValue, encodeBase64RouteValue } from "./base64_route_value.util";

export const SECURE_STORAGE_MANAGE_FILES_ROUTE = ["/secure-storage", "manage-files"] as const;
export const SECURE_STORAGE_PREVIEW_FILE_ROUTE = ["/secure-storage", "preview"] as const;
export const SECURE_STORAGE_UPLOAD_FILES_ROUTE = ["/secure-storage", "upload-files"] as const;
export const SECURE_STORAGE_MERCHANT_ID_QUERY_PARAM = "merchantId";
export const SECURE_STORAGE_FILE_ID_QUERY_PARAM = "fileId";
export const SECURE_STORAGE_PRODUCT_LICENSE_ID_QUERY_PARAM = "productLicenseId";
export const SECURE_STORAGE_EVIDENCE_REQUIREMENT_ID_QUERY_PARAM = "evidenceRequirementId";
export const SECURE_STORAGE_PREVIEW_OPENED_IN_NEW_TAB_QUERY_PARAM = "newTab";

export type SecureStorageMerchantRouteParseResult = {
    merchantId: number | null;
    errorCode: "missing" | "invalid" | null;
};

export type SecureStoragePreviewRouteParseResult = {
    merchantId: number | null;
    fileId: number | null;
    errorCode: "missing_merchant" | "invalid_merchant" | "missing_file" | "invalid_file" | null;
};

export type SecureStorageManageFileRouteParseResult = {
    merchantId: number | null;
    fileId: number | null;
    errorCode: "missing_merchant" | "invalid_merchant" | "invalid_file" | null;
};

export type SecureStorageEvidenceItemUploadRouteParseResult = {
    merchantId: number | null;
    productLicenseId: number | null;
    evidenceRequirementId: number | null;
    errorCode: "missing" | "invalid" | null;
};

export type SecureStorageMerchantRouteContext = "manage" | "upload" | "generic";

const buildEncodedPositiveIntegerParam = (value: number | null | undefined): string | null => {
    if (typeof value !== "number" || !Number.isSafeInteger(value) || value <= 0) {
        return null;
    }

    return encodeBase64RouteValue(String(value));
};

const parseEncodedPositiveIntegerParam = (value: string | null | undefined): {
    value: number | null;
    errorCode: "missing" | "invalid" | null;
} => {
    if (typeof value !== "string" || !value.trim()) {
        return {
            value: null,
            errorCode: "missing"
        };
    }

    const decoded = decodeBase64RouteValue(value);
    const parsed = Number(decoded);

    if (!Number.isSafeInteger(parsed) || parsed <= 0) {
        return {
            value: null,
            errorCode: "invalid"
        };
    }

    return {
        value: parsed,
        errorCode: null
    };
};

export const buildSecureStorageMerchantQueryParams = (merchantId: number | null | undefined): Params => {
    const encodedMerchantId = buildEncodedPositiveIntegerParam(merchantId);
    return encodedMerchantId
        ? {
              [SECURE_STORAGE_MERCHANT_ID_QUERY_PARAM]: encodedMerchantId
          }
        : {};
};

export const buildSecureStorageEvidenceItemUploadQueryParams = (
    merchantId: number | null | undefined,
    productLicenseId: number | null | undefined,
    evidenceRequirementId: number | null | undefined
): Params => {
    const encodedMerchantId = buildEncodedPositiveIntegerParam(merchantId);
    const encodedProductLicenseId = buildEncodedPositiveIntegerParam(productLicenseId);
    const encodedEvidenceRequirementId = buildEncodedPositiveIntegerParam(evidenceRequirementId);
    if (!encodedMerchantId || !encodedProductLicenseId || !encodedEvidenceRequirementId) {
        return {};
    }

    return {
        [SECURE_STORAGE_MERCHANT_ID_QUERY_PARAM]: encodedMerchantId,
        [SECURE_STORAGE_PRODUCT_LICENSE_ID_QUERY_PARAM]: encodedProductLicenseId,
        [SECURE_STORAGE_EVIDENCE_REQUIREMENT_ID_QUERY_PARAM]: encodedEvidenceRequirementId
    };
};

export const buildSecureStoragePreviewQueryParams = (
    merchantId: number | null | undefined,
    fileId: number | null | undefined,
    openedInNewTab = false
): Params => {
    const encodedMerchantId = buildEncodedPositiveIntegerParam(merchantId);
    const encodedFileId = buildEncodedPositiveIntegerParam(fileId);
    if (!encodedMerchantId || !encodedFileId) {
        return {};
    }

    const queryParams: Params = {
        [SECURE_STORAGE_MERCHANT_ID_QUERY_PARAM]: encodedMerchantId,
        [SECURE_STORAGE_FILE_ID_QUERY_PARAM]: encodedFileId
    };

    if (openedInNewTab) {
        queryParams[SECURE_STORAGE_PREVIEW_OPENED_IN_NEW_TAB_QUERY_PARAM] = encodeBase64RouteValue("1");
    }

    return queryParams;
};

export const buildSecureStorageManageFileQueryParams = (
    merchantId: number | null | undefined,
    fileId: number | null | undefined
): Params => buildSecureStoragePreviewQueryParams(merchantId, fileId);

export const isSecureStoragePreviewOpenedInNewTab = (value: string | null | undefined): boolean => {
    return decodeBase64RouteValue(value) === "1";
};

export const parseSecureStorageMerchantRouteParam = (
    value: string | null | undefined
): SecureStorageMerchantRouteParseResult => {
    const parsed = parseEncodedPositiveIntegerParam(value);
    if (parsed.errorCode === "missing") {
        return {
            merchantId: null,
            errorCode: "missing"
        };
    }

    if (parsed.errorCode === "invalid") {
        return {
            merchantId: null,
            errorCode: "invalid"
        };
    }

    return {
        merchantId: parsed.value,
        errorCode: null
    };
};

export const parseSecureStorageMerchantId = (value: string | null | undefined): number | null => {
    return parseSecureStorageMerchantRouteParam(value).merchantId;
};

export const parseSecureStorageEvidenceItemUploadRouteParams = (
    merchantValue: string | null | undefined,
    productLicenseValue: string | null | undefined,
    evidenceRequirementValue: string | null | undefined
): SecureStorageEvidenceItemUploadRouteParseResult => {
    const parsedValues = [merchantValue, productLicenseValue, evidenceRequirementValue]
        .map(parseEncodedPositiveIntegerParam);
    if (parsedValues.some((result) => result.errorCode === "missing")) {
        return { merchantId: null, productLicenseId: null, evidenceRequirementId: null, errorCode: "missing" };
    }
    if (parsedValues.some((result) => result.errorCode === "invalid")) {
        return { merchantId: null, productLicenseId: null, evidenceRequirementId: null, errorCode: "invalid" };
    }

    return {
        merchantId: parsedValues[0].value,
        productLicenseId: parsedValues[1].value,
        evidenceRequirementId: parsedValues[2].value,
        errorCode: null
    };
};

export const parseSecureStoragePreviewRouteParams = (
    merchantValue: string | null | undefined,
    fileValue: string | null | undefined
): SecureStoragePreviewRouteParseResult => {
    const merchantResult = parseEncodedPositiveIntegerParam(merchantValue);
    if (merchantResult.errorCode === "missing") {
        return {
            merchantId: null,
            fileId: null,
            errorCode: "missing_merchant"
        };
    }
    if (merchantResult.errorCode === "invalid") {
        return {
            merchantId: null,
            fileId: null,
            errorCode: "invalid_merchant"
        };
    }

    const fileResult = parseEncodedPositiveIntegerParam(fileValue);
    if (fileResult.errorCode === "missing") {
        return {
            merchantId: merchantResult.value,
            fileId: null,
            errorCode: "missing_file"
        };
    }
    if (fileResult.errorCode === "invalid") {
        return {
            merchantId: merchantResult.value,
            fileId: null,
            errorCode: "invalid_file"
        };
    }

    return {
        merchantId: merchantResult.value,
        fileId: fileResult.value,
        errorCode: null
    };
};

export const parseSecureStorageManageFileRouteParams = (
    merchantValue: string | null | undefined,
    fileValue: string | null | undefined
): SecureStorageManageFileRouteParseResult => {
    const merchantResult = parseEncodedPositiveIntegerParam(merchantValue);
    if (merchantResult.errorCode === "missing") {
        return { merchantId: null, fileId: null, errorCode: "missing_merchant" };
    }
    if (merchantResult.errorCode === "invalid") {
        return { merchantId: null, fileId: null, errorCode: "invalid_merchant" };
    }

    if (typeof fileValue !== "string" || !fileValue.trim()) {
        return { merchantId: merchantResult.value, fileId: null, errorCode: null };
    }

    const fileResult = parseEncodedPositiveIntegerParam(fileValue);
    if (fileResult.errorCode !== null) {
        return { merchantId: merchantResult.value, fileId: null, errorCode: "invalid_file" };
    }

    return { merchantId: merchantResult.value, fileId: fileResult.value, errorCode: null };
};

export const getSecureStoragePreviewRouteErrorMessage = (
    parseResult: SecureStoragePreviewRouteParseResult
): string | null => {
    if (parseResult.errorCode === "missing_merchant") {
        return "A merchant selection is required before a Secure Storage file preview can be opened.";
    }

    if (parseResult.errorCode === "invalid_merchant") {
        return "The Secure Storage preview merchant link is invalid. Use a valid preview link from Manage Files.";
    }

    if (parseResult.errorCode === "missing_file") {
        return "A file selection is required before a Secure Storage preview can be opened.";
    }

    if (parseResult.errorCode === "invalid_file") {
        return "The Secure Storage preview file link is invalid. Use a valid preview link from Manage Files.";
    }

    return null;
};

export const getSecureStorageMerchantRouteErrorMessage = (
    parseResult: SecureStorageMerchantRouteParseResult,
    context: SecureStorageMerchantRouteContext = "generic"
): string | null => {
    if (parseResult.errorCode === "missing") {
        if (context === "manage") {
            return "A merchant selection is required before Secure Storage files can be managed.";
        }

        if (context === "upload") {
            return "A merchant selection is required before Secure Storage files can be uploaded.";
        }

        return "A merchant selection is required before using Secure Storage.";
    }

    if (parseResult.errorCode === "invalid") {
        if (context === "manage") {
            return "The Secure Storage Manage Files merchant link is invalid. Use a valid deep link from the dashboard or assessment.";
        }

        if (context === "upload") {
            return "The Secure Storage Upload Files merchant link is invalid. Use a valid deep link from the dashboard or assessment.";
        }

        return "The Secure Storage merchant link is invalid. Use a valid merchant deep link from the dashboard or assessment.";
    }

    return null;
};
